/**
 * /shop — Customer browse hub.
 *
 * Solves the discovery gap surfaced 2026-02-28: a buyer arriving at
 * the platform had to know that dogs live at /marketplace, services
 * at /find-pros, transport at /transport, storefronts at /storefronts,
 * and events at /events. Now there's a single grid that funnels every
 * customer to the right surface.
 *
 * Six large persona-card-style tiles:
 *   - Dogs                  → /marketplace
 *   - Upcoming litters      → /marketplace/upcoming
 *   - Service providers     → /find-pros
 *   - Transport             → /transport
 *   - Storefronts           → /storefronts
 *   - Events                → /events
 *
 * Each tile has a one-line pitch + a price/range badge so the visitor
 * can do quick mental triage. The page is public — anonymous users can
 * land here from the global "Shop" nav entry without logging in.
 */
import React from "react";
import { isNativeApp } from "@/lib/nativeBridge";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { DogIcon as Dog, EggIcon as Egg, ScissorsIcon as Scissors, TruckIcon as Truck, StorefrontIcon as Storefront, ConfettiIcon as Confetti, ArrowRightIcon as ArrowRight, SparkleIcon as Sparkle } from "@phosphor-icons/react";

const TILES = [
  {
    key: "armstrong",
    icon: Storefront,
    badge: "House brand · dog food & merch",
    title: "Armstrong K9 Shop",
    body: "Order Diamond High Energy Dog Food, branded merch, and custom orders direct from Armstrong K9 Registry. Stripe-protected checkout, delivery in select zones.",
    cta: "Shop Armstrong",
    to: "/armstrong",
    testid: "shop-tile-armstrong",
    highlight: true,
  },
  {
    key: "dogs",
    icon: Dog,
    badge: "From verified kennels",
    title: "Buy a dog",
    body: "Browse audited pedigrees, take live video tours, pay through Stripe escrow with a 14-day dispute window.",
    cta: "Browse the marketplace",
    to: "/marketplace",
    testid: "shop-tile-dogs",
  },
  {
    key: "upcoming",
    icon: Egg,
    badge: "Reserve before they're born",
    title: "Upcoming litters",
    body: "See pairings planned + currently expecting. Place a waitlist deposit to claim your pup ahead of the whelp.",
    cta: "See upcoming litters",
    to: "/marketplace/upcoming",
    testid: "shop-tile-upcoming",
  },
  {
    key: "service_providers",
    icon: Scissors,
    badge: "From $40 / session",
    title: "Hire a trainer, groomer, walker, or sitter",
    body: "Vetted independent pros. Single or recurring sessions, Stripe-protected, reviews drive ranking. 88% to the provider.",
    cta: "Find a service pro",
    to: "/find-pros",
    testid: "shop-tile-find-pros",
    highlight: true,
  },
  {
    key: "transport",
    icon: Truck,
    badge: "USDA-licensed haulers",
    title: "Book transport",
    body: "Vetted haulers carrying $300K+ liability bid on your route. Escrow funds release on confirmed delivery.",
    cta: "Request a quote",
    to: "/transport/request",
    testid: "shop-tile-transport",
  },
  {
    key: "storefronts",
    icon: Storefront,
    badge: "Food · merch · custom orders",
    title: "Shop kennel storefronts",
    body: "Independent /store/{slug} pages from kennels and dog-product brands. Stripe Tax + Connect built in.",
    cta: "Browse storefronts",
    to: "/storefronts",
    testid: "shop-tile-storefronts",
  },
  {
    key: "events",
    icon: Confetti,
    badge: "RSVP + ticket via Stripe",
    title: "Events &amp; meet-ups",
    body: "Conformation shows, working trials, meet-and-greets, breed-club socials. RSVP free or pay an entry fee.",
    cta: "Browse events",
    to: "/events",
    testid: "shop-tile-events",
  },
];

export default function Shop() {
  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-20">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson mb-3" data-testid="shop-eyebrow">
          Everything you can buy or book on Armstrong K9
        </p>
        <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          One platform. Six ways to shop.
        </h1>
        <p className="mt-5 text-[15px] text-[#3F3F3F] max-w-2xl">
          Dogs, services, transport, storefront products, and events — every customer-side surface on the registry, in one place. Each is paid through Stripe with venue-level dispute resolution baked in.
        </p>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="shop-grid">
          {TILES.map((t) => {
            const Icon = t.icon;
            const ringClass = t.highlight
              ? "border-[#C9A227] shadow-[0_18px_36px_-20px_rgba(201,162,39,0.4)]"
              : "border-[#EFEFEF]";
            return (
              <Link
                to={t.to}
                key={t.key}
                className={`group rounded-2xl border ${ringClass} bg-white p-6 flex flex-col transition hover:border-ink hover:shadow-[0_12px_28px_-16px_rgba(0,0,0,0.22)]`}
                data-testid={t.testid}
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[#FBF7EC] text-gold-dark">
                    <Icon size={18} weight="duotone" />
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A00] font-bold">{t.badge}</span>
                </div>
                <h3 className="font-spectral text-2xl text-ink leading-tight mb-2" dangerouslySetInnerHTML={{ __html: t.title }} />
                <p className="text-[13px] text-[#3F3F3F] leading-snug flex-1 mb-5">{t.body}</p>
                <div className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-ink group-hover:text-crimson">
                  {t.cta} <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
                </div>
              </Link>
            );
          })}
        </div>

        {/* Help strip for buyers who don't know which to pick.
            Hidden natively — subscription pricing (App Store 3.1.1). */}
        {!isNativeApp() && (
        <div className="mt-16 rounded-2xl border border-[#E8D9A6] bg-[#FBF7EC] p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid="shop-cta-strip">
          <div className="flex items-start gap-3 max-w-2xl">
            <Sparkle size={20} weight="duotone" className="text-gold-dark flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-spectral text-xl text-ink mb-1">Not sure where to start?</p>
              <p className="text-[13.5px] text-[#3F3F3F] leading-snug">
                A <strong>Buyer Pass</strong> ($4.99/mo or $39/yr) unlocks unlimited inquiries across the marketplace + Cmd-K global search across every surface above.
              </p>
            </div>
          </div>
          <Link to="/billing?plan=buyer_pass">
            <Button className="rounded-full bg-ink hover:bg-crimson text-white h-11 px-6 whitespace-nowrap" data-testid="shop-buyer-pass-cta">
              Get a Buyer Pass <ArrowRight size={14} className="ml-1.5" />
            </Button>
          </Link>
        </div>
        )}
      </div>
    </AppShell>
  );
}
